import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'

/*
 * Order matters twice over: tokens before the component CSS that reads them, and -- new, and the
 * subtle one -- Nexus 2.0 before the legacy tokens it is replacing. Everything imported here is
 * VENDORED and none of it is editable from this repo (CONSOLES.md 5: one design system, vendored,
 * not forked). A change belongs in the design system package.
 *
 * Nexus 2.0, page-wide. GENERATED from the vendored release by tools/emit-root-tokens.mjs --
 * the same tokens the design-template previews already use, lifted out of the canvas scope onto
 * :root so they reach the whole console, with the polarity mirrored (the design system makes dark
 * the default; this console is light by default).
 *
 * ---------------------------------------------------------------------------------------------
 * IMPORTED BEFORE tokens.css, AND THE ORDER IS THE WHOLE POINT.
 *
 * Where the two systems define the same token NAME they do not always mean the same thing, and
 * `--brand-orange` is the case that proves it. Nexus 2.0's own guide is explicit:
 *
 *     "NthAI brand orange --brand-orange #E06018 appears ONLY in brand marks (the wordmark
 *      period); never as a UI state."   -- nexus-design-system/docs/design-system.md
 *
 * The legacy system means the opposite by that name: app.css reads `--brand-orange` in TWELVE
 * places and every one is a UI state -- the primary button, the focus border, the active tab,
 * the toggle, the checkbox accent-color, the active stepper dot. Letting Nexus win on that name
 * would repurpose a brand-mark-only token as the console's action colour across all twelve, in
 * direct breach of the rule, and the near-identical hex (#E06018 against #E87722) means nothing
 * would look wrong while the semantics quietly inverted.
 *
 * So legacy wins on collisions, for now, by being imported second. Nexus still supplies all 84
 * tokens the legacy system does not define, and dark mode is unaffected: the dark block is
 * `:root[data-theme='dark']`, specificity (0,2,0), which beats a plain `:root` whatever the
 * order. The tokens that SHOULD carry the action colour are `--accent` / `--accent-hover`, and
 * moving app.css's twelve usages onto them is a migration step, not an import order -- it is on
 * the list in the operational-layer package because app.css has to be rewritten against Nexus
 * vocabulary regardless.
 *
 * That migration is also what retires the older #E87722. It has NOT happened yet; claiming it
 * had was the first version of this comment, and it was wrong.
 * ---------------------------------------------------------------------------------------------
 *
 * tokens.css is still imported, and still load-bearing, because Nexus 2.0 does not define 46 of
 * the 59 tokens this app's stylesheets consume -- no monospace family, no semantic status pairs,
 * no focus ring, no dense radius or density scale. That layer is being authored upstream rather
 * than shimmed here. As it lands, this file's second import gets shorter and tokens.css gets
 * closer to deletable.
 */
import './vendor/nexus-design-system/v1.0.0/tokens.root.css'
import './styles/tokens.css'
import './styles/app.css'
/*
 * The dark-mode bridge. Binds the legacy token names app.css reads onto Nexus 2.0, for dark mode
 * only, so the appearance toggle actually repaints. Contains no colour values at all -- every
 * declaration is a var() into the vendored file above, or a color-mix() over one. See its header
 * for why a vocabulary mapping cannot live upstream, and for the four tokens it has to derive
 * because Nexus has no answer for them yet.
 *
 * Imported after app.css so it can also fix the handful of vendored rules whose light-mode
 * assumption lives in the selector rather than in a token (the frosted top bar, the code block,
 * the toast). It adds scoped selectors; it redefines none.
 */
import './styles/nexus-bridge.css'
// Chrome controls the Admin Console does not have (the top-bar popovers). Kept OUT of the
// vendored files on purpose -- see the header of chrome.css. Imported last so it can rely on
// the tokens, and it adds selectors rather than redefining vendored ones.
import './components/chrome/chrome.css'
// Select and dialog styling the vendored layers lack. Same contract as chrome.css; see its header.
import './components/ui/controls.css'
import './components/ui/surfaces.css'

const root = document.getElementById('root')
if (!root) throw new Error('index.html has no #root element')

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
